import { useCallback, useEffect, useMemo, useState } from 'react'
import { Flex, IconButton, IconPlus, Text } from '@vtex/shoreline'
import { FormGroup, InputHidden } from '~/components/unform'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { useCampaignForm } from '../CampaignForm/hooks/useCampaignForm'

import EmptyState from './EmptyState'
import ScreenGroup from './ScreenGroup'
import Modal from './Modal'

import s from './styles.module.css'

export default function ScreensGroups({
  handleFormChange,
  formRef
}: ScreensProps) {
  const [modalState, setModalState] = useState({
    open: false,
    isEditing: false,
    groupToEdit: null as { group: ScreenGroup; index: number } | null
  })

  const { t } = useAPPTranslation()
  const { screenGroups, setScreenGroups, campaignPermissions } =
    useCampaignForm()

  const selectedScreensIds: string = useMemo(() => {
    let ids: string[] = []
    if (screenGroups?.length > 0) {
      const allIds = screenGroups
        ?.flatMap(group => group?.screens?.map(screen => screen.id) ?? [])
        ?.filter(Boolean)

      ids = [...new Set(allIds)]
    }

    return ids?.length > 0 ? ids?.join(',') : null
  }, [screenGroups])

  const handleModalState = useCallback(
    ({
      open,
      isEditing,
      groupToEdit
    }: {
      open?: boolean
      isEditing?: boolean
      groupToEdit?: { group: ScreenGroup; index: number } | null
    }) => {
      setModalState(prevState => ({
        ...prevState,
        open: open ?? prevState.open,
        isEditing: isEditing ?? prevState.isEditing,
        groupToEdit: groupToEdit ?? prevState.groupToEdit
      }))
    },
    [setModalState]
  )

  useEffect(() => {
    formRef?.current?.setFieldValue('places.place_ids', selectedScreensIds)
    handleFormChange()
  }, [selectedScreensIds])

  return (
    <FormGroup className={s.container}>
      <InputHidden name="place_ids" value={selectedScreensIds} />
      <Flex direction="column" gap="var(--sl-space-5)">
        <Flex align="center" justify="space-between">
          <Text variant="emphasis">
            {t('rm:campaign.create.ScreenGroups.title')}
          </Text>
          <IconButton
            label={t('rm:campaign.create.ScreenGroups.iconLabel')}
            size="normal"
            onClick={() => handleModalState({ open: true })}
          >
            <IconPlus />
          </IconButton>
        </Flex>
        {!screenGroups || screenGroups.length === 0 ? (
          <EmptyState handleClick={() => handleModalState({ open: true })} />
        ) : (
          <Flex
            direction="column"
            gap="var(--sl-space-2)"
            className={s.overflowAuto}
          >
            {screenGroups.map((group, index) => (
              <ScreenGroup
                key={index}
                group={group}
                setScreenGroups={setScreenGroups}
                isStandard={campaignPermissions?.cost_type === 'standard'}
                handleEditGroup={() =>
                  handleModalState({
                    open: true,
                    isEditing: true,
                    groupToEdit: { group, index }
                  })
                }
              />
            ))}
          </Flex>
        )}
        <Modal
          allowPlacesSelect={campaignPermissions?.allow_places_select}
          isStandard={campaignPermissions?.cost_type === 'standard'}
          open={modalState?.open}
          isEditing={modalState?.isEditing}
          groupToEdit={modalState?.groupToEdit}
          closeModal={() =>
            handleModalState({
              open: false,
              isEditing: false,
              groupToEdit: null
            })
          }
        />
      </Flex>
    </FormGroup>
  )
}
